import React, { FunctionComponent, useMemo } from 'react';
import { IDisplayRoleContext, DisplayRoleContext, useDisplayRole } from 'app/components/DisplayRoleContext';
import { STUDENT, TEACHER } from 'app/constants/DisplayRoles';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingType } from 'app/models/rtc/booking/constants';
import { useRtcState } from 'app/containers/rtc/RtcStateContext';
import { connect } from 'react-redux';
import * as Schema from 'app/constants/schemas';
import { Entity, EntityStringId } from 'app/types/entity';
import { IParticipant } from 'app/constants/schemas/rtc/Participant';
import { getEntityOfType } from 'app/models/entities/selectors';
import { isParticipantHost } from 'app/models/rtc/selectors';
import LessonBookingRole from 'app/constants/LessonBookingRole';

interface IProps {
  callerParticipant?: Entity<IParticipant>
}

const useBookingDisplayRole = ({ callerParticipant }: IProps): IDisplayRoleContext['role'] => {
  const bookingState = useBookingState();
  const { role } = useDisplayRole();

  switch (bookingState?.bookingType) {
    case BookingType.Lesson:
      return bookingState.lessonRole === LessonBookingRole.Teacher ? TEACHER : STUDENT; // TODO: What about Other?
    case BookingType.Meeting:
      return isParticipantHost(callerParticipant) ? TEACHER : STUDENT;
    case BookingType.TeacherRegistration:
    case BookingType.TestCall:
    case undefined:
      return role;
  }
};

const BookingDisplayRoleProvider: FunctionComponent<IProps> = ({ children, callerParticipant }) => {
  const role = useBookingDisplayRole({ callerParticipant });

  const context = useMemo(() => ({
    role,
    override: () => {},
  }), [role]);

  return (
    <DisplayRoleContext.Provider value={context}>
      {children}
    </DisplayRoleContext.Provider>
  );
};

const BookingDisplayRoleProviderConnected = connect((state, { callerId }: { callerId?: EntityStringId }) => ({
  callerParticipant: getEntityOfType(state, Schema.Participant, callerId),
}))(BookingDisplayRoleProvider);

/**
 * Overrides display role (teacher, student) in context of lesson
 * and meeting bookings
 *
 * In lessons, teacher teaching given lesson is given `TEACHER`
 * display role.
 *
 * In meetings, pariticipants that are hosts are given `TEACHER`
 * display role.
 */
const BookingDisplayRoleProviderInjectDependencies: FunctionComponent = (props) => {
  const { caller } = useRtcState();
  return <BookingDisplayRoleProviderConnected callerId={caller?.id} {...props} />;
};

export default BookingDisplayRoleProviderInjectDependencies;
